import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import ArrowOutward from "@mui/icons-material/ArrowOutward";
import ArrowBack from "@mui/icons-material/ArrowBack";
import { blogPosts, archivedPosts } from "../data/blog-content";
import { SiteShell, NextChapter } from "../components/BrandPrimitives";
import { PAGE_META } from "../utils/shareMeta";
import "../styles/brand-editorial.css";

export function BrandJournalPage() {
  return (
    <SiteShell {...PAGE_META.journal}>
      <section className="brand-editorial-hero brand-container">
        <span className="brand-eyebrow">The Lab / Journal</span>
        <h1>
          The next era
          <br />
          <span>of work.</span>
        </h1>
        <div className="brand-editorial-intro">
          <p>
            Notes from building a digital workforce.
            <br />
            Ideas, questions, and convictions from the lab.
          </p>
          <span className="brand-caption">Weekly notes from the Pioneer AI Lab</span>
        </div>
      </section>
      <section
        className="brand-journal-list brand-container"
        aria-label="Journal articles"
      >
        {blogPosts.map((post) => (
          <Link
            to={"/blog/" + post.slug}
            key={post.slug}
            className="brand-journal-item"
          >
            <img
              className="brand-journal-art"
              src={post.image}
              alt={post.imageAlt}
              loading="lazy"
              width="1200"
              height="630"
            />
            <div>
              <span className="brand-caption">{post.category}</span>
              <h2>{post.title}</h2>
              <p>{post.excerpt}</p>
              <span className="brand-journal-date">
                {post.date} / {post.readTime}
              </span>
            </div>
            <ArrowOutward />
          </Link>
        ))}
      </section>
      <NextChapter
        title="A conviction worth building."
        text="See the research agenda and the roles that will make it real."
        to="/careers"
        label="Explore the roles"
      />
    </SiteShell>
  );
}

function initialArticleContent(slug, suppliedContent) {
  if (typeof suppliedContent === "string") return suppliedContent;
  if (typeof document === "undefined") return null;
  try {
    const preload = JSON.parse(document.getElementById("grupa-journal-content")?.textContent || "null");
    return preload?.slug === slug && typeof preload.content === "string" ? preload.content : null;
  } catch {
    return null;
  }
}

export function BrandJournalPost({ initialContent } = {}) {
  const { slug } = useParams();
  const post = [...blogPosts, ...archivedPosts].find((p) => p.slug === slug);
  const isArchived = archivedPosts.includes(post);
  const related = blogPosts.filter((p) => p.slug !== slug);
  const preloadedContent = initialArticleContent(slug, initialContent);
  const [state, setState] = useState(() => preloadedContent === null
    ? { status: "loading", content: "" }
    : { status: "ready", content: preloadedContent });
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    if (!post) return undefined;
    if (preloadedContent !== null && retry === 0) {
      setState({ status: "ready", content: preloadedContent });
      return undefined;
    }
    const controller = new AbortController();
    setState({ status: "loading", content: "" });
    fetch("/blog-posts/" + post.contentFile, { signal: controller.signal })
      .then((response) => {
        if (
          !response.ok ||
          response.headers.get("content-type")?.includes("text/html")
        )
          throw new Error("Article unavailable");
        return response.text();
      })
      .then((content) => setState({ status: "ready", content }))
      .catch((error) => {
        if (error.name !== "AbortError")
          setState({ status: "error", content: "" });
      });
    return () => controller.abort();
  }, [post, retry, preloadedContent]);

  return (
    <SiteShell
      title={
        post ? post.title + " - GrupaAI Journal" : "Article not found - GrupaAI"
      }
      description={post?.excerpt || "Explore ideas from the GrupaAI Journal."}
      image={post?.image || PAGE_META.journal.image}
      path={"/blog/" + slug}
    >
      <section className="brand-editorial-hero brand-container brand-article-header">
        <Link to="/blog" className="brand-text-link brand-article-back">
          <ArrowBack fontSize="small" />
          Journal
        </Link>
        {post?.category && (
          <span className="brand-eyebrow">{post.category}</span>
        )}
        <h1>{post?.title || "Article not found."}</h1>
        {post && (
          <>
            <p className="brand-article-deck">{post.excerpt}</p>
            <div className="brand-article-meta">
              <span>{post.author}</span>
              <span>{post.date}</span>
              <span>{post.readTime}</span>
            </div>
          </>
        )}
      </section>
      {post && (
        <article className="brand-prose brand-journal-article">
          {isArchived && (
            <p className="brand-archive-note">
              From the archive. For our current direction, explore the{" "}
              <Link to="/blog">new GrupaAI Journal</Link>.
            </p>
          )}
          {state.status === "loading" ? (
            <p role="status">Loading the article...</p>
          ) : state.status === "error" ? (
            <div className="brand-empty">
              <h2>The article could not be loaded.</h2>
              <button
                className="brand-button"
                onClick={() => setRetry((n) => n + 1)}
              >
                Try again
              </button>
            </div>
          ) : (
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {state.content}
            </ReactMarkdown>
          )}
        </article>
      )}
      {post && (
        <section className="brand-related brand-container">
          <span className="brand-eyebrow">Continue reading</span>
          <div>
            {related.map((item) => (
              <Link to={"/blog/" + item.slug} key={item.slug}>
                <span className="brand-caption">{item.category}</span>
                <h2>{item.title}</h2>
                <ArrowOutward />
              </Link>
            ))}
          </div>
        </section>
      )}
    </SiteShell>
  );
}
